import React, { useEffect, useRef, useState } from 'react';
import _ from 'lodash';

import { Button } from '@lwt-helix/buttons';
import BaseForm from '@lwt-helix/form-schema';
import StickyFooter from '@lwt-helix/sticky-footer';

import { setStateData } from '../../../common/StateHelper';
import { userFormSchema, userFormUiSchema } from './userFormSchema';
import RemoveUser from '../RemoveUser/RemoveUser';
import { useSelector } from 'react-redux';

const UserForm = ({
    activeUser,
    securityGroups,
    onCancel,
    refreshTable,
    updateInactiveDate,
    updateSecurityGroupUsers,
    updateInvitationWithRoleId
}) => {
    const permissions = useSelector(_state => _state.authorizationReducer.permissions.permissions);
    const isManageUsers = 'U-U' in permissions;
    const [state, setState] = useState({
        formSchema: {},
        user: undefined,
        isSecurityGroupChanged: false,
        isUserStatusChanged: false
    });

    const saveButtonRef = useRef();

    useEffect(() => {
        setState({
            user: activeUser,
            isSecurityGroupChanged: false,
            isUserStatusChanged: false
        })
    }, [activeUser]);

    useEffect(() => {
        state.user && populateState();
    }, [state.user]);

    const saveUser = () => {
        if (state.isSecurityGroupChanged) {
            if (state.user.isStatusEnabled) {
                let securityGroup = securityGroups.find(securityGroup => securityGroup.id === state.user.securityGroupId);
                let users = [...securityGroup.users];
                if (!users.some(user => user.userId === state.user.id)) {
                    users.push({ userId: state.user.id });
                }
                updateSecurityGroupUsers(securityGroup.id, users);
            }
            else {
                updateInvitationWithRoleId(state.user.id, { roleId: state.user.securityGroupId });
            }
            setStateData('isSecurityGroupChanged', false, setState);
        }
        if (state.isUserStatusChanged) {
            updateInactiveDate(state.user.userStatus === 'ACTIVE', state.user.id);
            setStateData('isUserStatusChanged', false, setState);
        }
    };

    const handleChange = e => {
        const formData = { ...e.formData };
        setState(prevState => {
            const nextState = { ...prevState };
            nextState.user = {
                ...prevState.user,
                userStatus: formData.userStatus,
                securityGroupId: formData.securityGroupId
            };
            if (formData.securityGroupId !== prevState.user?.securityGroupId) {
                nextState.isSecurityGroupChanged = true;
            } 
            if (formData.userStatus !== prevState.user?.userStatus) {
                nextState.isUserStatusChanged = true;
            }
            return nextState;
        });
    };

    const hrWidget = () => {
        return <hr />
    };

    const widgets = { hrWidget };

    const populateState = () => {
        const form = {
            schema: userFormSchema,
            uiSchema: userFormUiSchema(state.user.hasOffice, state.user.isStatusEnabled, isManageUsers),
            widgets: widgets,
            saveButtonRef: saveButtonRef,
            formData: { ...state.user },
            onChange: handleChange,
            idPrefix: 'user',
            id: 'user-form'
        };

        const sgIds = [], sgNames = [];
        _.sortBy(securityGroups, ['name'])
            .forEach(sg => {
                sgIds.push(sg.id);
                sgNames.push(sg.name);
            });
        form.schema.properties['securityGroupId'].enum = sgIds;
        form.schema.properties['securityGroupId'].enumNames = sgNames;

        setStateData('formSchema', form, setState);
    };

    const onRemove = () => {
        refreshTable();
        onCancel();
        setState(state => ({
            ...state,
            isDirty: false,
            user: null
        }));
    };

    const stickyFooterProps = {
        isPanelFooter: true,
        columnProps: [
            {
                sm: 4,
                align: 'left',
                children: <RemoveUser userId={state.user?.id} onRemove={onRemove} />
            },
            {
                sm: 8,
                align: 'right',
                children:
                    <>
                        <Button dataLwtId="cancel-button" onClick={onCancel} color="secondary">Cancel</Button>
                        <Button
                            dataLwtId="save-button"
                            ref={saveButtonRef}
                            onClick={() => saveUser()}
                            color="primary"
                            disabled={!(state.isSecurityGroupChanged || state.isUserStatusChanged)}
                        >
                            Save
                        </Button>
                    </>
            }
        ]
    };

    return (
        <>
            {state.user &&
                <>
                    <BaseForm {...state.formSchema} />
                    <StickyFooter {...stickyFooterProps} />
                </>
            }
        </>
    );
};

export default UserForm;
